/**
 * Quiet site footer. Wordmark + nav + contextual cue. No CTAs, no marketing,
 * no newsletter signup. This is where you stop talking.
 */
export function SiteFooter() {
  return (
    <footer className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-32 sm:px-10 sm:pt-32">
      <div className="border-t border-border pt-10">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
          {/* Brand block */}
          <div>
            <a
              href="/"
              className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em] text-text"
            >
              <span className="size-1.5 rounded-full bg-emerald shadow-[0_0_8px_rgba(127,255,140,0.55)]" />
              NOERACLE
            </a>
            <p className="mt-4 max-w-md font-serif text-[18px] leading-snug text-m70">
              The first pull-based price oracle for Stellar. Sub-500&nbsp;ms
              freshness, verified inside your transaction.
            </p>
          </div>

          {/* Nav columns */}
          <nav
            className="flex flex-wrap gap-x-10 gap-y-6 font-mono text-[10px] uppercase tracking-[0.22em] text-m55 sm:justify-end"
            aria-label="Footer"
          >
            <Col title="PRODUCT">
              <FootLink href="https://docs.noeracle.org/get-started/quickstart" target="_blank" rel="noopener">
                Quickstart
              </FootLink>
              <FootLink href="https://docs.noeracle.org/integration/patterns" target="_blank" rel="noopener">
                Integration
              </FootLink>
              <FootLink href="https://docs.noeracle.org/reference/sdk" target="_blank" rel="noopener">
                SDK reference
              </FootLink>
            </Col>
            <Col title="DESIGN">
              <FootLink href="https://docs.noeracle.org/concepts/architecture" target="_blank" rel="noopener">
                Architecture
              </FootLink>
              <FootLink href="https://docs.noeracle.org/concepts/threat-model" target="_blank" rel="noopener">
                Threat model
              </FootLink>
              <FootLink href="https://docs.noeracle.org/project/roadmap" target="_blank" rel="noopener">
                Roadmap
              </FootLink>
            </Col>
            <Col title="LIVE">
              <FootLink href="/status/">Status</FootLink>
              <FootLink href="/metrics/">Metrics</FootLink>
              <FootLink href="https://www.npmjs.com/package/@noeracle/sdk" rel="noopener">
                npm
              </FootLink>
              <FootLink href="https://github.com/noeracle/noeracle" rel="noopener">
                GitHub
              </FootLink>
            </Col>
          </nav>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 font-mono text-[9.5px] uppercase tracking-[0.22em] text-m30">
          <span>© {new Date().getFullYear()} NOERACLE · v0 TESTNET PROTOTYPE</span>
          <span>BUILT FOR STELLAR</span>
        </div>
      </div>
    </footer>
  );
}

function Col({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-m30">{title}</span>
      <div className="flex flex-col gap-2.5 text-m70">{children}</div>
    </div>
  );
}

function FootLink({
  href,
  rel,
  target,
  children,
}: {
  href: string;
  rel?: string;
  target?: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      rel={rel}
      target={target}
      className="transition-colors hover:text-emerald"
    >
      {children}
    </a>
  );
}
